import React, { useEffect, useState } from 'react';
import api from '../../services/api';

const STUDENT_CSV_EXAMPLE = `Nom;Prénom;INE;Classe;Email;Date de naissance;Téléphone;Adresse;Email parent
DUPONT;Marie;1234567890A;NDRC1;marie.dupont@exemple.fr;12/03/2008;0612345678;12 rue des Lilas, 75001 Paris;dupont.parent@exemple.fr
MARTIN;Lucas;0987654321B;NDRC1;lucas.martin@exemple.fr;05/11/2007;;;martin.parent@exemple.fr`;

const TEACHER_CSV_EXAMPLE = `Nom;Prénom;Email;Téléphone;Classe
BERNARD;Sophie;sophie.bernard@exemple.fr;0140000000;NDRC1`;

// Télécharge un exemple CSV (BOM UTF-8 pour un affichage correct des accents dans Excel)
function downloadCsvExample(filename, content) {
  const blob = new Blob(['\uFEFF' + content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function ExampleToggle({ open, onToggle, onDownload, color, content }) {
  const colors = {
    blue:   { link: 'text-blue-700 hover:text-blue-900', btn: 'border-blue-300 text-blue-700 hover:bg-blue-100', pre: 'border-blue-200' },
    purple: { link: 'text-purple-700 hover:text-purple-900', btn: 'border-purple-300 text-purple-700 hover:bg-purple-100', pre: 'border-purple-200' },
  }[color] || {};
  return (
    <>
      <div className="flex items-center justify-between mt-3">
        <button type="button" onClick={onToggle} className={`text-xs underline ${colors.link}`}>
          {open ? 'Masquer l\'exemple' : 'Voir un exemple de fichier'}
        </button>
        <button type="button" onClick={onDownload}
          className={`text-xs font-medium bg-white border px-3 py-1.5 rounded-lg transition-colors ${colors.btn}`}>
          ⬇ Télécharger l'exemple (.csv)
        </button>
      </div>
      {open && (
        <pre className={`mt-3 bg-white border ${colors.pre} rounded p-3 text-xs text-gray-700 overflow-x-auto leading-relaxed`}>
          {content}
        </pre>
      )}
    </>
  );
}

function ResultsPanel({ result }) {
  if (!result) return null;
  return (
    <div className="space-y-4">
      {result.warning && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg text-sm">
          ⚠️ {result.warning}
        </div>
      )}
      <div className="grid grid-cols-4 gap-3">
        {[
          { key: 'created', label: 'Créés',       color: 'green' },
          { key: 'updated', label: 'Mis à jour',  color: 'blue'  },
          { key: 'skipped', label: 'Ignorés',     color: 'yellow'},
          { key: 'errors',  label: 'Erreurs',     color: 'red'   },
        ].map(({ key, label, color }) => (
          <div key={key} className={`bg-${color}-50 border border-${color}-200 rounded-lg p-4 text-center`}>
            <p className={`text-2xl font-bold text-${color}-700`}>{result.summary[key]}</p>
            <p className={`text-sm text-${color}-600 mt-0.5`}>{label}</p>
          </div>
        ))}
      </div>

      {['created', 'updated', 'skipped', 'errors'].map(key => (
        result[key]?.length > 0 && (
          <div key={key} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
              <p className="text-sm font-medium text-gray-700 capitalize">{key === 'created' ? 'Créés' : key === 'updated' ? 'Mis à jour' : key === 'skipped' ? 'Ignorés' : 'Erreurs'} ({result[key].length})</p>
            </div>
            <ul className="divide-y divide-gray-100 max-h-52 overflow-y-auto">
              {result[key].map((s, i) => (
                <li key={i} className="px-4 py-2 text-sm flex gap-2">
                  <span className="font-medium text-gray-800">{s.line ? `Ligne ${s.line} — ` : ''}{s.name}</span>
                  {s.email && <span className="text-gray-400">{s.email}</span>}
                  {s.reason && <span className="text-red-500 ml-auto">{s.reason}</span>}
                </li>
              ))}
            </ul>
          </div>
        )
      ))}
    </div>
  );
}

function StudentImport() {
  const [file, setFile] = useState(null);
  const [schoolYearId, setSchoolYearId] = useState('');
  const [schoolYears, setSchoolYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showExample, setShowExample] = useState(false);

  useEffect(() => {
    api.get('/admin/school-years').then(r => {
      setSchoolYears(r.data);
      const active = r.data.find(y => y.isActive);
      if (active) setSchoolYearId(active.id);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return setError('Sélectionnez un fichier CSV');
    setError(''); setResult(null); setLoading(true);
    const form = new FormData();
    form.append('file', file);
    if (schoolYearId) form.append('schoolYearId', schoolYearId);
    try {
      const res = await api.post('/admin/import/students', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data); setFile(null); e.target.reset();
    } catch (err) {
      let msg = err.response?.data?.error || 'Erreur lors de l\'import';
      if (err.response?.data?.detected_headers) msg += ` — En-têtes : ${err.response.data.detected_headers.join(', ')}`;
      setError(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm font-medium text-blue-800 mb-2">Format CSV (export Pronote)</p>
        <div className="bg-white border border-blue-200 rounded p-2 font-mono text-xs text-gray-700 overflow-x-auto whitespace-nowrap">
          Nom ; Prénom ; <strong>INE</strong> ; <strong>Classe</strong> ; Email ; Date de naissance ; Téléphone ; Adresse ; Email parent
        </div>
        <p className="text-xs text-blue-600 mt-2">
          Obligatoires : <strong>Nom, Prénom, INE</strong>. Colonne <strong>Classe</strong> : affecte et crée la classe automatiquement.
          Mot de passe temporaire : <strong>StageLink2026!</strong>
        </p>
        <ExampleToggle
          color="blue"
          open={showExample}
          onToggle={() => setShowExample(v => !v)}
          onDownload={() => downloadCsvExample('exemple-import-eleves.csv', STUDENT_CSV_EXAMPLE)}
          content={STUDENT_CSV_EXAMPLE}
        />
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fichier CSV</label>
          <input type="file" accept=".csv,.txt" required onChange={e => setFile(e.target.files[0])}
            className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Année scolaire <span className="text-gray-400 font-normal">(utilisée si le CSV ne précise pas de classe)</span>
          </label>
          <select value={schoolYearId} onChange={e => setSchoolYearId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Année active par défaut</option>
            {schoolYears.map(y => <option key={y.id} value={y.id}>{y.label}{y.isActive ? ' (active)' : ''}</option>)}
          </select>
        </div>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}
        <button type="submit" disabled={loading}
          className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-5 py-2.5 rounded-lg">
          {loading ? 'Import en cours...' : 'Importer les étudiants'}
        </button>
      </form>

      <ResultsPanel result={result} />
    </div>
  );
}

function TeacherImport() {
  const [file, setFile] = useState(null);
  const [schoolYearId, setSchoolYearId] = useState('');
  const [schoolYears, setSchoolYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [showExample, setShowExample] = useState(false);

  useEffect(() => {
    api.get('/admin/school-years').then(r => {
      setSchoolYears(r.data);
      const active = r.data.find(y => y.isActive);
      if (active) setSchoolYearId(active.id);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return setError('Sélectionnez un fichier CSV');
    setError(''); setResult(null); setLoading(true);
    const form = new FormData();
    form.append('file', file);
    if (schoolYearId) form.append('schoolYearId', schoolYearId);
    try {
      const res = await api.post('/admin/import/teachers', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data); setFile(null); e.target.reset();
    } catch (err) {
      let msg = err.response?.data?.error || 'Erreur lors de l\'import';
      if (err.response?.data?.detected_headers) msg += ` — En-têtes : ${err.response.data.detected_headers.join(', ')}`;
      setError(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-5">
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <p className="text-sm font-medium text-purple-800 mb-2">Format CSV enseignants</p>
        <div className="bg-white border border-purple-200 rounded p-2 font-mono text-xs text-gray-700 overflow-x-auto whitespace-nowrap">
          Nom ; Prénom ; <strong>Email</strong> ; Téléphone ; <strong>Classe</strong>
        </div>
        <p className="text-xs text-purple-600 mt-2">
          Obligatoires : <strong>Nom, Prénom, Email</strong>. Colonne <strong>Classe</strong> : affecte l'enseignant à la classe (créée si inexistante).
          Mot de passe temporaire : <strong>StageLink2026!</strong>
        </p>
        <ExampleToggle
          color="purple"
          open={showExample}
          onToggle={() => setShowExample(v => !v)}
          onDownload={() => downloadCsvExample('exemple-import-enseignants.csv', TEACHER_CSV_EXAMPLE)}
          content={TEACHER_CSV_EXAMPLE}
        />
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-gray-200 p-5 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fichier CSV</label>
          <input type="file" accept=".csv,.txt" required onChange={e => setFile(e.target.files[0])}
            className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-purple-50 file:text-purple-700 hover:file:bg-purple-100" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Année scolaire <span className="text-gray-400 font-normal">(pour la création des classes)</span>
          </label>
          <select value={schoolYearId} onChange={e => setSchoolYearId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Année active par défaut</option>
            {schoolYears.map(y => <option key={y.id} value={y.id}>{y.label}{y.isActive ? ' (active)' : ''}</option>)}
          </select>
        </div>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}
        <button type="submit" disabled={loading}
          className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium px-5 py-2.5 rounded-lg">
          {loading ? 'Import en cours...' : 'Importer les enseignants'}
        </button>
      </form>

      <ResultsPanel result={result} />
    </div>
  );
}

export default function ImportTab() {
  const [sub, setSub] = useState('students');
  return (
    <div>
      <div className="flex gap-4 mb-6 border-b border-gray-200">
        {[
          { id: 'students', label: 'Étudiants' },
          { id: 'teachers', label: 'Enseignants' },
        ].map(t => (
          <button key={t.id} onClick={() => setSub(t.id)}
            className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${sub === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {sub === 'students' && <StudentImport />}
      {sub === 'teachers' && <TeacherImport />}
    </div>
  );
}
